/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import { memo, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useIntl, FormattedMessage } from "react-intl";
import { useParams, useHistory } from "react-router-dom";

import { Grid, Typography, TextField } from "@material-ui/core";

import { Group, Label, Icon, Button, SelectorModal } from "..";
import { useToast } from "../../context/toast";
import useAuthSWR from "../../hooks/useAuthSWR";
import useCaptureVideoInfo from "../../hooks/useCaptureVideoInfo";
import useHyperlinksByLocale from "../../hooks/useHyperlinksByLocale";
import { group } from "../../styles/layout";
import { FetchedList, Video, ActivityMode } from "../../types";
import dateTimeFormat from "../../utils/dateTimeFormat";
import PreviewVideoDialog from "../PreviewVideoDialog";
import { State as ViewStateType } from "../ViewState";

import {
  disabled,
  fileBlock,
  fileField,
  hoverFile,
  actionField,
  emptyFileBlock,
  selectFileButton,
  closeButton,
  emptyIcon,
  emptyText,
  button,
  errorFileBlock,
  exclamationIcon,
  link,
} from "./styles";

export interface SelectFileValues {
  loomApiKey: string;
  loomJobId: string;
  videos: any;
}

export interface Ref {
  setPreviewVideoUrl: (url: string) => void;
}

interface Props {
  editable: boolean;
  isLoomInfoEditable: boolean;
  activityMode: ActivityMode;
  enablePreviewVod: boolean;
}

const MEDIA_API_PATH = "/videos/inputs";
const PAGE_COUNT = 6;

const SelectFileGroup: React.ForwardRefRenderFunction<Ref, Props> = (
  { editable, activityMode, isLoomInfoEditable, enablePreviewVod }: Props,
  ref
): JSX.Element => {
  const intl = useIntl();
  const { id } = useParams<{ id?: string }>();
  const { register, control, errors, clearErrors, setValue } = useFormContext();
  const { push } = useHistory();
  const showToast = useToast();
  const captureVideoInfo = useCaptureVideoInfo();
  const {
    LOOM_ACCOUNT_REGISTRATION_URL,
    LOOM_BEFORE_YOU_START_URL,
    LOOM_CHECK_ENCODING_STATUAS_URL,
  } = useHyperlinksByLocale();

  const [openFileModal, setOpenFileModal] = useState<boolean>(false);
  const [viewMediaStatus, setViewMediaStatus] = useState<ViewStateType>(id ? "loading" : "success");
  const [mediaSelectPage, setMediaSelectPage] = useState(1);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string>();

  const isPlaybackMode = activityMode === ActivityMode.PlaybackMode;

  // If use optional rendering to register a field which didn't render at first time, the field can't be reset with data and would be defaultValue
  const { loomApiKey, loomJobId } = useWatch<SelectFileValues>({
    control,
    name: ["loomApiKey", "loomJobId"],
    defaultValue: {
      loomApiKey: "",
      loomJobId: "",
    },
  });
  const { fields, remove } = useFieldArray({
    control,
    name: "videos",
    keyName: "field_id",
  });

  const { state: mediaSwrState, isValidating: isMediaValidating, mutate: fetchVideos, data: media } = useAuthSWR<
    FetchedList<Video>
  >(openFileModal ? `${MEDIA_API_PATH}?limit=${PAGE_COUNT}&page=${mediaSelectPage || 1}` : null, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    dedupingInterval: 0, // [MB-190] default dedupingInterval 2000ms was too long and might get stale value from cache.
  });

  useEffect(() => {
    if (isMediaValidating) {
      setViewMediaStatus("loading");
    } else {
      setViewMediaStatus(mediaSwrState);
    }
  }, [isMediaValidating, mediaSwrState]);

  useImperativeHandle(ref, () => ({
    setPreviewVideoUrl: (url: string) => setPreviewVideoUrl(url),
  }));

  const handleSelectFile = async (records: any[]) => {
    try {
      clearErrors("videos");
      // eslint-disable-next-line
      const recordsWithDuration = await Promise.all(
        records.map(async (record) => {
          if (record.duration) {
            return record;
          }
          const videoInfo = await captureVideoInfo(record.pre_signed_url);
          return {
            ...record,
            ...videoInfo,
          };
        })
      );
      setOpenFileModal(false);
      if (recordsWithDuration.length > 0) {
        setPreviewVideoUrl(recordsWithDuration[0].pre_signed_url);
      }
      setValue("videos", recordsWithDuration, { shouldDirty: true });
    } catch (error) {
      console.error("> Vod to live detail > handleSelectFile: ", error);
      showToast(intl.formatMessage({ id: "general.error" }), { type: "error" });
      fetchVideos();
    }
  };

  return (
    <>
      <Group
        css={group}
        title={intl.formatMessage({ id: "vod-to-live-page.file" })}
        {...(isPlaybackMode && {
          subtitle: intl.formatMessage(
            {
              id: "vod-to-live-page.file.description",
            },
            {
              a: (chunk) => (
                <a href={LOOM_ACCOUNT_REGISTRATION_URL} target="_blank" rel="noreferrer noopener" css={link}>
                  {chunk}
                </a>
              ),
            }
          ),
        })}
        {...(!id &&
          !isPlaybackMode && {
            actions: [
              {
                icon: "add",
                label: intl.formatMessage({
                  id: "vod-to-live-page.select-a-file",
                }),
                color: "white",
                style: selectFileButton,
                disabled: Boolean(fields.length),
                onClick: () => {
                  setOpenFileModal(true);
                },
              },
            ],
          })}
        {...(!editable &&
          isPlaybackMode && {
            actions: [
              {
                label: intl.formatMessage({
                  id: "vod-to-live-page.preview-vod",
                }),
                icon: "reveal",
                color: "white",
                style: selectFileButton,
                disabled: !enablePreviewVod,
                onClick: () => setOpenPreview(true),
              },
            ],
          })}
      >
        {!isPlaybackMode && (
          <>
            {!fields.length || Boolean(errors?.videos) ? (
              <Typography align="center" css={[emptyFileBlock, Boolean(errors?.videos) && errorFileBlock]}>
                {errors?.videos ? (
                  <>
                    <Icon name="exclamation" viewBox="0 0 48 48" css={exclamationIcon} />
                    {errors?.videos?.message}
                  </>
                ) : (
                  <FormattedMessage id="vod-to-live-page.empty-file" />
                )}
              </Typography>
            ) : (
              fields.map((field, index) => (
                <div css={fileBlock} key={field.field_id}>
                  <div css={fileField}>
                    <Typography variant="body2" css={hoverFile} onClick={() => setOpenPreview(true)}>
                      {field?.display_name}
                    </Typography>
                  </div>
                  <div>{dateTimeFormat(field?.created_at, intl)}</div>
                  <div css={actionField}>
                    {!id && <Icon name="close" viewBox="0 0 48 48" css={closeButton} onClick={() => remove(index)} />}
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {isPlaybackMode && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Label
                text={intl.formatMessage({
                  id: "vod-to-live-page.loom-api-key",
                })}
                tip={intl.formatMessage(
                  {
                    id: "vod-to-live-page.loom-api-key.tooltip",
                  },
                  {
                    a: (chunk) => (
                      <a href={LOOM_BEFORE_YOU_START_URL} target="_blank" rel="noreferrer noopener" css={link}>
                        {chunk}
                      </a>
                    ),
                  }
                )}
              />
              <TextField
                css={disabled}
                fullWidth
                name="loomApiKey"
                inputRef={register({
                  required: true,
                })}
                inputProps={{
                  readOnly: !editable || (!!id && !isLoomInfoEditable),
                }}
                error={!!errors.loomApiKey}
                helperText={
                  errors.loomApiKey &&
                  errors.loomApiKey.type === "required" &&
                  intl.formatMessage({ id: "validation.required" })
                }
                defaultValue={loomApiKey}
              />
            </Grid>
            <Grid item xs={6}>
              <Label
                text={intl.formatMessage({
                  id: "vod-to-live-page.loom-job-id",
                })}
                tip={intl.formatMessage(
                  {
                    id: "vod-to-live-page.loom-job-id.tooltip",
                  },
                  {
                    a: (chunk) => (
                      <a href={LOOM_CHECK_ENCODING_STATUAS_URL} target="_blank" rel="noreferrer noopener" css={link}>
                        {chunk}
                      </a>
                    ),
                  }
                )}
              />
              <TextField
                css={disabled}
                fullWidth
                name="loomJobId"
                inputRef={register({
                  required: true,
                })}
                inputProps={{
                  readOnly: !editable || (!!id && !isLoomInfoEditable),
                }}
                error={!!errors.loomJobId}
                helperText={
                  errors.loomJobId &&
                  errors.loomJobId.type === "required" &&
                  intl.formatMessage({ id: "validation.required" })
                }
                defaultValue={loomJobId}
              />
            </Grid>
          </Grid>
        )}
      </Group>
      <SelectorModal
        open={openFileModal}
        title={intl.formatMessage({ id: "vod-to-live-page.select-a-file" })}
        label={intl.formatMessage({ id: "vod-to-live-page.file.count" }, { count: media?.paging?.total || 0 })}
        onSelect={handleSelectFile}
        onClose={() => setOpenFileModal(false)}
        onReload={() => fetchVideos()}
        selectedRows={fields}
        noDataRender={
          <>
            <Icon name="media" css={emptyIcon} />
            <div css={emptyText}>{intl.formatMessage({ id: "media.empty-files" })}</div>
            <Button icon="add" css={button} variant="contained" color="primary" onClick={() => push("/live/media")}>
              {intl.formatMessage({ id: "btn.file.upload-files" })}
            </Button>
          </>
        }
        tableProps={{
          headCells: [intl.formatMessage({ id: "media.file-name" }), intl.formatMessage({ id: "media.upload-time" })],
          bodyRows: media?.data || [],
          bodyCells: [{ key: "display_name" }, { key: "created_at", convertDateTime: true }],
          selectionType: "radio",
        }}
        paginationProps={{
          page: media?.paging?.current_page,
          perPage: media?.paging?.per_page,
          total: media?.paging?.total,
          onChange: (_, page) => setMediaSelectPage(page),
        }}
        viewStatus={viewMediaStatus}
      />
      <PreviewVideoDialog
        title={fields[0]?.display_name}
        src={previewVideoUrl}
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        onBackButtonClick={() => setOpenPreview(false)}
        native={!isPlaybackMode}
      />
    </>
  );
};

export default memo(forwardRef(SelectFileGroup));
